import { useActiveAnnouncement } from '@common/components/Announcement/useActiveAnnouncement';
import { FooterV2 } from '@common/components/Footer/FooterV2';
import { useLanguagePickerItems } from '@common/components/LanguageSwitcher/useLanguagePickerItems';
import { Routes } from '@common/constants';
import { useIsMinimalStyle } from '@common/contexts/MinimalStyleContext';
import { useIsKeyboardOpen } from '@common/hooks/useIsKeyboardOpen';
import { getCategoryListRoute } from '@features/Home/SearchFlow';
import { useHomeCategories } from '@features/Home/v2/useHomeCategories';
import { useTranslations } from '@lang';
import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'shared-routing';
import type {
  AppShellAnnouncementConfig,
  AppShellBannerConfig,
  MenuDrawerItem,
  MobileTabItem,
  SideNavItem,
} from 'voyager';
import { AppShell } from 'voyager';
import { Logo } from './components/Logo';
import type { LayoutProps } from './GeneralLayout';
import { ShellWrapper, TopBar } from './GeneralLayoutV2.styles';
import { VoyagerThemeBridge } from './VoyagerThemeBridge';

const bookingFlowRoutes: Routes[] = [
  Routes.EVENT_DETAIL,
  Routes.PRODUCT_DETAIL,
  Routes.RATE_DETAIL,
  Routes.SPA_STAYS_DETAIL,
  Routes.ROOM_DETAIL,
  Routes.SPA_DAY_DETAIL,
  Routes.SPA_TREATMENT_DETAIL,
  Routes.FACILITY_DETAIL,
  Routes.VOUCHER_DETAIL,
  Routes.SPA_CLASSES_BOOKING,
  Routes.SPA_DAY_BOOKING,
  Routes.SPA_TREATMENT_BOOKING,
  Routes.FACILITY_BOOKING,
];

export function GeneralLayoutV2({
  children,
  property,
  language,
  basketCount = 0,
  guest,
  termsAndConditionPage = null,
  privacyAndCookiePolicyPage = null,
  cancellationPage = null,
}: LayoutProps) {
  const router = useRouter();
  const { t } = useTranslations();
  const isMinimalStyle = useIsMinimalStyle();
  const isKeyboardOpen = useIsKeyboardOpen();

  const isAuthRoute =
    router.pathname === Routes.SIGNIN ||
    router.pathname === Routes.REGISTER ||
    router.pathname === Routes.FORGOTTEN_PASSWORD;

  const isDiningBookingRoute =
    router.pathname === Routes.TABLES_BOOK || router.pathname === Routes.TABLES_BOOK_RESTAURANT;

  const isRoomRateBookingRoute =
    router.pathname === Routes.ROOM_BOOKING ||
    router.pathname === Routes.RATE_BOOKING ||
    router.pathname === Routes.SPA_STAYS_BOOKING;

  const isSearchRoute = router.pathname === Routes.SEARCH;

  const isBookingFlowRoute = bookingFlowRoutes.includes(router.pathname as Routes);

  const hideTopBar =
    isMinimalStyle ||
    router.pathname === Routes.HOME ||
    isSearchRoute ||
    router.pathname === Routes.MY_ACCOUNT ||
    router.pathname.startsWith(`${Routes.MY_ACCOUNT}/`) ||
    isAuthRoute ||
    isDiningBookingRoute ||
    isRoomRateBookingRoute ||
    isBookingFlowRoute;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);

  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  const navigate = useCallback(
    (pathname?: string) => {
      setIsMenuOpen(false);
      setIsLanguageOpen(false);
      if (pathname) router.push({ pathname });
    },
    [router]
  );

  const goHome = useCallback(() => navigate(Routes.HOME), [navigate]);
  const goBasket = useCallback(() => navigate(Routes.BASKET), [navigate]);

  const accountRoute = guest ? Routes.MY_ACCOUNT : Routes.SIGNIN;
  const goAccount = useCallback(() => navigate(accountRoute), [navigate, accountRoute]);

  const toggleMenu = useCallback(
    () =>
      setIsMenuOpen((v) => {
        if (!v) setIsLanguageOpen(false);
        return !v;
      }),
    []
  );

  const closeLanguage = useCallback(() => setIsLanguageOpen(false), []);
  const openLanguage = useCallback(() => {
    setIsMenuOpen(false);
    setIsLanguageOpen(true);
  }, []);

  const { categories } = useHomeCategories({ property, language });

  const availableLanguages = useMemo(
    () => [...(property.additionalLanguages ?? []), property.defaultLanguage],
    [property.additionalLanguages, property.defaultLanguage]
  );

  const languageItems = useLanguagePickerItems({
    availableLanguages,
    currentLanguage: language,
  });
  const showLanguagePicker = languageItems.length > 1;

  const announcement = useActiveAnnouncement({
    slug: property.slug,
    language,
    fallbackImageUrl: property.homepage?.heroBackgroundImage?.url,
  });

  const bannerConfig: AppShellBannerConfig | undefined =
    announcement && announcement.type !== 'dialog' && !isMinimalStyle
      ? {
          heading: announcement.heading,
          body: announcement.body,
          dismissible: announcement.dismissible,
          onDismiss: announcement.dismissible ? announcement.onDismiss : undefined,
          closeLabel: t('journeypicker.close'),
        }
      : undefined;

  const announcementConfig: AppShellAnnouncementConfig | undefined =
    announcement && announcement.type === 'dialog' && !isMinimalStyle
      ? {
          open: announcement.open,
          onClose: announcement.onClose,
          heading: announcement.heading,
          body: announcement.body,
          imageUrl: announcement.imageUrl,
          validFor: announcement.validFor,
          cta: announcement.cta,
          closeLabel: t('journeypicker.close'),
        }
      : undefined;

  const menuItems: MenuDrawerItem[] = useMemo(
    () =>
      categories.map((cat) => ({
        id: cat.id,
        label: cat.label,
        imageUrl: cat.imageUrl ?? null,
        onSelect: () => navigate(getCategoryListRoute(cat.id)),
      })),
    [categories, navigate]
  );

  const navItems: SideNavItem[] = useMemo(
    () => [
      { id: 'home', label: t('nav.home'), icon: 'home', onClick: goHome },
      {
        id: 'menu',
        label: t('basket.menu'),
        icon: 'menu',
        active: isMenuOpen,
        onClick: toggleMenu,
      },
      {
        id: 'basket',
        label: t('app.basket'),
        icon: 'basket',
        badge: basketCount,
        onClick: goBasket,
      },
    ],
    [goHome, toggleMenu, isMenuOpen, goBasket, basketCount, t]
  );

  const bottomNavItems: SideNavItem[] = useMemo(
    () => [
      {
        id: 'account',
        label: guest ? t('nav.account') : t('common.signIn'),
        icon: 'account',
        onClick: goAccount,
        hideLabel: true,
      },
      ...(showLanguagePicker
        ? [
            {
              id: 'language',
              label: t('nav.language'),
              icon: 'language' as const,
              active: isLanguageOpen,
              onClick: openLanguage,
              hideLabel: true,
            },
          ]
        : []),
    ],
    [goAccount, guest, t, showLanguagePicker, isLanguageOpen, openLanguage]
  );

  const mobileNavItems: MobileTabItem[] = useMemo(
    () => [
      { id: 'home', label: t('nav.home'), icon: 'home', onClick: goHome },
      {
        id: 'menu',
        label: t('basket.menu'),
        icon: 'menu',
        active: isMenuOpen,
        onClick: toggleMenu,
      },
      {
        id: 'basket',
        label: t('app.basket'),
        icon: 'basket',
        badge: basketCount,
        onClick: goBasket,
      },
      {
        id: 'account',
        label: guest ? t('nav.account') : t('common.signIn'),
        icon: 'account',
        onClick: goAccount,
      },
    ],
    [goHome, toggleMenu, isMenuOpen, goBasket, basketCount, goAccount, guest, t]
  );

  const activeLanguageLabel = useMemo(
    () => languageItems.find((item) => item.active)?.name,
    [languageItems]
  );

  const siteFooter = (
    <FooterV2
      property={property}
      language={language}
      termsAndConditionPage={termsAndConditionPage}
      privacyAndCookiePolicyPage={privacyAndCookiePolicyPage}
      cancellationPage={cancellationPage}
    />
  );

  return (
    <ShellWrapper>
      <VoyagerThemeBridge />
      <AppShell
        navItems={navItems}
        bottomNavItems={bottomNavItems}
        mobileNavItems={mobileNavItems}
        navigationLabel={t('aria.mainNavigation')}
        banner={bannerConfig}
        announcement={announcementConfig}
        menu={{
          open: isMenuOpen,
          onClose: closeMenu,
          title: t('basket.menu'),
          closeLabel: t('journeypicker.close'),
          items: menuItems,
          width: '400px',
          noImageLabel: t('basket.noImageLabel'),
          languageSelector:
            showLanguagePicker && activeLanguageLabel
              ? {
                  label: activeLanguageLabel,
                  active: isLanguageOpen,
                  onSelect: openLanguage,
                }
              : undefined,
        }}
        languagePicker={
          showLanguagePicker
            ? {
                open: isLanguageOpen,
                onClose: closeLanguage,
                title: t('languageSwitcher.heading'),
                closeLabel: t('aria.languageSwitcher'),
                items: languageItems,
              }
            : undefined
        }
        footer={
          isMinimalStyle || isDiningBookingRoute || isRoomRateBookingRoute || isSearchRoute
            ? undefined
            : siteFooter
        }
        hideNavigation={isMinimalStyle || isKeyboardOpen}
      >
        {!hideTopBar && (
          <TopBar>
            <Logo logo={property.logo} name={property.name} />
          </TopBar>
        )}

        {children}
      </AppShell>
    </ShellWrapper>
  );
}
